import { getImageProps, type ImageLoaderProps } from 'next/image';

const MZSTATIC_THUMB = /^(https:\/\/[a-z0-9-]+\.mzstatic\.com\/image\/thumb\/.+\/\d+x\d+[a-z]*)\.(?:jpe?g|png)$/i;

/** Apple's image CDN renders any thumb size as WebP when the extension asks for it (~55% smaller than the JPEG). */
export function mzstaticWebp(url: string): string {
  return url.replace(MZSTATIC_THUMB, '$1.webp');
}

const MZSTATIC_THUMB_SIZED = /^(https:\/\/[a-z0-9-]+\.mzstatic\.com\/image\/thumb\/.+\/)\d+x\d+([a-z]*)\.(?:jpe?g|png|webp)$/i;

/** The header icon renders at most 128 CSS px, so a 256px WebP covers 2x screens at about half the bytes of the 512px one. */
export function mzstaticIcon(url: string): string {
  return url.replace(MZSTATIC_THUMB_SIZED, '$1256x256$2.webp');
}

const MZSTATIC_THUMB_DIMS = /^(https:\/\/[a-z0-9-]+\.mzstatic\.com\/image\/thumb\/.+\/)(\d+)x(\d+)([a-z]*)\.(?:jpe?g|png|webp)$/i;

export function isMzstaticThumb(url: string): boolean {
  return MZSTATIC_THUMB_DIMS.test(url);
}

/**
 * next/image loader that asks Apple's CDN for the exact width instead of routing
 * through Vercel's optimizer. next/image already passes the 2x width for retina
 * srcset entries; the height keeps the source's aspect ratio. Apple's WebP carries
 * ~3.4 KB of fixed overhead, so below 128px its JPEG is the smaller file.
 */
export function mzstaticLoader({ src, width }: ImageLoaderProps): string {
  const m = MZSTATIC_THUMB_DIMS.exec(src);
  if (!m) return src;
  const [, base, w, h, suffix] = m;
  const height = Math.max(1, Math.round((width * Number(h)) / Number(w)));
  return `${base}${width}x${height}${suffix}.${width < 128 ? 'jpg' : 'webp'}`;
}

/**
 * The page's LCP image (first screenshot) through our own /_next/image: it
 * rides the page's existing connection instead of waiting on a new TLS
 * handshake to mzstatic, and Chrome gets AVIF. One fixed width (2x of 192 =
 * 384) so each screenshot is one transformation; sources are ~320px wide and
 * the optimizer never enlarges, so the pixels match the direct WebP.
 */
export function sameOriginScreenshot(url: string): string {
  return getImageProps({ src: url, alt: '', width: 192, height: 288, quality: 75 }).props.src;
}
